/**
 * Drones (WORKBOARD §12, row I2; owner's rulings 3–5).
 *
 * A drone is tasked from a person and spent once flown. Ruling 25 as the owner clarified it: the
 * operator is busy only while **tasking** it (setting its course — `beginDroneTasking`; a person
 * at layer 0 will do this on a tablet) and while **launching** it (`droneLaunchTime` seconds) —
 * it stands still then, no walking, jumping or firing. Once the drone is away it flies its route on
 * its own: a fixed speed, a fixed height above the terrain under it (low: it reveals less than
 * today's probe), no loiter; it is spent when the route ends, when its battery (endurance) runs
 * out, or when a bullet brings it down — its operator's fate no longer matters. Below it, a cone of sight: living enemies on its planet within
 * its ground radius that it has a clear line to become sightings (`by: 'drone'`) in the same
 * perishable list the towers write, each with the tick it was made.
 *
 * Terrain reveal (ruling 4) belongs to the layer-2 fog and arrives with the probe as a drone
 * (row I3); layer 0 has no terrain fog to reveal into.
 *
 * Pure and deterministic (dmath only). `Match.launchDrone` is the action; `Match.stepAction`
 * flies them every tick and lets them look every TOWER_SIGHT_PERIOD ticks.
 */

import type { DronePayload, DroneState, GameState, PlanetState, PlayerState, Sighting, StrikeTask, Vec3 } from '../types';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { gustedField, stormWind, windSeconds } from './wind';
import { ACTION_WIND, DRONE_WEATHER } from '../constants/action';
import { weatherHere } from './weather';
import { dacos, dsin } from './dmath';
import { getSurfaceRadius } from './surface';
import { hasLineOfSight, losEyePoint } from './los';
import { personSetOf } from './scaleProfile';
import { personEyePoint, personLineOfSight } from './fineTerrain';
import { crownHides } from './foliage';

export type DroneEvent =
  | { kind: 'droneLaunched'; drone: DroneState }
  /** 62.19 (e): the weather held a scout where it is, or called a strike back (why: a storm, or the wind); `cleared` when it flies on. */
  | { kind: 'droneGrounded'; droneId: number; why: 'storm' | 'wind'; recalled?: true }
  | { kind: 'droneCleared'; droneId: number }
  | { kind: 'droneSpent'; droneId: number; end: NonNullable<DroneState['end']> }
  /** 65.16: a strike drone left its route and dived. */
  | { kind: 'droneDived'; droneId: number; pos: Vec3 };

const angle = (a: Vec3, b: Vec3): number => dacos(Math.max(-1, Math.min(1, vecDot(a, b))));

/** Arc length of each leg of the route on a planet of `radius`. */
export function legLengths(route: readonly Vec3[], radius: number): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < route.length; i++) out.push(angle(route[i], route[i + 1]) * radius);
  return out;
}

/** Where along its route the drone is: the ground direction under it. */
export function droneGroundDir(drone: DroneState, radius: number): Vec3 {
  const legs = legLengths(drone.route, radius);
  let left = drone.flown;
  for (let i = 0; i < legs.length; i++) {
    if (left <= legs[i] || i === legs.length - 1) {
      const a = drone.route[i], b = drone.route[i + 1];
      const theta = angle(a, b);
      if (theta < 1e-12) return { ...a };
      const t = Math.max(0, Math.min(1, legs[i] > 0 ? left / legs[i] : 0));
      const s = dsin(theta);
      return vecNormalize(vecAdd(vecScale(a, dsin((1 - t) * theta) / s), vecScale(b, dsin(t * theta) / s)));
    }
    left -= legs[i];
  }
  return { ...drone.route[0] };
}

/** The drone's position: its ground point lifted to its altitude above the terrain there. */
export function dronePos(drone: DroneState, state: Pick<GameState, 'planets' | 'config'>): Vec3 {
  if (drone.dive) return drone.dive.pos;   // 65.16: a strike drone off its route, diving
  const planet = state.planets[drone.planetIndex];
  const altitude = personSetOf(state).droneAltitude;
  // 62.65 (d): kept per drone object (a flown drone is a new object every tick, stepDrones) while the planet, its heightmap
  // and the altitude stand — every bullet's every substep asked for every enemy drone (the page's largest allocation
  // site at 50 a side with drones on). Shared: callers read it, never write into it.
  const hit = posKept.get(drone);
  if (hit && hit.planet === planet && hit.heightmap === planet.heightmap && hit.altitude === altitude) return hit.pos;
  const dir = droneGroundDir(drone, planet.radius);
  const r = getSurfaceRadius(planet, dir) + altitude;
  const pos = vecAdd(planet.position, vecScale(dir, r));
  posKept.set(drone, { planet, heightmap: planet.heightmap, altitude, pos });
  return pos;
}
const posKept = new WeakMap<DroneState, { planet: PlanetState; heightmap: Float32Array; altitude: number; pos: Vec3 }>();

export type LaunchCheck = { ok: true } | { ok: false; reason: string; code: string };

/** May `operator` launch a drone along `waypoints` (unit directions on its own planet) now? With the match (62.19 (e)), not into a storm or a gale. */
export function canLaunchDrone(operator: PlayerState, waypoints: readonly Vec3[], state?: GameState, seconds = 0): LaunchCheck {
  const ready = canTaskDrone(operator, state, seconds);
  if (!ready.ok) return ready;
  if (waypoints.length === 0) return { ok: false, reason: 'Set a route first', code: 'no route' };
  return { ok: true };
}

/**
 * 62.19 (e): does the weather ground a drone at a place now? A storm there (the weather switch; the cell overhead with
 * weatherCells) — 'storm'; a wind there of at least DRONE_WEATHER.gale × a storm's wind in the open (the wind switch: the
 * field with its gust, no ground shelter at a drone's height) — 'wind'; else null. Pure.
 */
export function droneWeather(state: GameState, planetIndex: number, dir: Vec3, seconds: number): 'storm' | 'wind' | null {
  if (!state.config.weather) return null;
  if (weatherHere(state, planetIndex, dir, seconds) === 'storm') return 'storm';
  const planet = state.planets[planetIndex];
  if (state.config.wind && planet) {
    const w = gustedField(state, planetIndex, dir, seconds);
    if (w && vecLength(w) >= DRONE_WEATHER.gale * stormWind(planet)) return 'wind';
  }
  return null;
}

/** May `operator` start tasking a drone (open its tablet) now? With the match (62.19 (e)), not in a storm or a gale: the tablet says why. */
export function canTaskDrone(operator: PlayerState, state?: GameState, seconds = 0): LaunchCheck {
  if (!operator.alive || operator.airborne) return { ok: false, reason: 'Cannot launch now', code: 'not grounded' };
  if (operator.mount) return { ok: false, reason: 'Dismount first', code: 'mounted' };
  if ((operator.droneLaunchLeft ?? 0) > 0) return { ok: false, reason: 'Still launching', code: 'launching' };
  if (!((operator.drones ?? 0) > 0)) return { ok: false, reason: 'No drones left', code: 'none' };
  if (state) {
    const why = droneWeather(state, operator.planetIndex, operator.surfaceDir, seconds);
    if (why) return { ok: false, reason: why === 'storm' ? 'Grounded: a storm overhead' : 'Grounded: the wind is too strong', code: 'weather' };
  }
  return { ok: true };
}

/** Is this unit busy with a drone — tasking it or launching it — and so standing still? */
export function droneBusy(p: Pick<PlayerState, 'droneTasking' | 'droneLaunchLeft'>): boolean {
  return !!p.droneTasking || (p.droneLaunchLeft ?? 0) > 0;
}

/** Start tasking: the unit stands still while it sets the course (until it launches or cancels). */
export function beginDroneTasking(state: GameState, operatorId: number): GameState {
  return { ...state, players: state.players.map(p => p.id === operatorId ? { ...p, droneTasking: true } : p) };
}

/** Stop tasking without launching. */
export function cancelDroneTasking(state: GameState, operatorId: number): GameState {
  return { ...state, players: state.players.map(p => p.id === operatorId && p.droneTasking ? { ...p, droneTasking: undefined } : p) };
}

/** The launch clock: every launching operator's remaining launch time runs down by `dt`. */
export function tickDroneLaunches(state: GameState, dt: number): GameState {
  if (!state.players.some(p => (p.droneLaunchLeft ?? 0) > 0)) return state;
  return { ...state, players: state.players.map(p => (p.droneLaunchLeft ?? 0) > 0 ? { ...p, droneLaunchLeft: Math.max(0, p.droneLaunchLeft! - dt) || undefined } : p) };
}

/** Launch: the drone takes off from where its operator stands; one drone is spent; tasking ends and the operator stands still for the launch. */
export function launchDrone(state: GameState, operatorId: number, waypoints: readonly Vec3[], strike?: StrikeTask, payload?: DronePayload): { state: GameState; drone: DroneState } {
  const op = state.players.find(p => p.id === operatorId)!;
  const drones = state.drones ?? [];
  const drone: DroneState = {
    id: drones.length, operatorId, teamIndex: op.teamIndex, planetIndex: op.planetIndex,
    route: [vecNormalize(op.surfaceDir), ...waypoints.map(w => vecNormalize(w))], flown: 0, age: 0, alive: true, body: 'drone',
    // 65.16: a strike drone's operator carries strike drones; 65.19: or it carries an explosive payload (a charge, spent)
    ...(op.droneKind === 'strike' || payload === 'explosive' ? { variant: 'strike' as const } : {}),
    ...(strike ? { strike } : {}),
  };
  return {
    drone,
    state: {
      ...state,
      drones: [...drones, drone],
      players: state.players.map(p => p.id === operatorId ? {
        ...p, drones: (p.drones ?? 0) - 1, droneTasking: undefined, droneLaunchLeft: personSetOf(state).droneLaunchTime,
        ...(payload === 'explosive' && p.droneKind !== 'strike' ? { charges: Math.max(0, (p.charges ?? 0) - 1) } : {}),   // 65.19
      } : p),
    },
  };
}

/** End a drone: it is spent. */
export function endDrone(state: GameState, droneId: number, end: NonNullable<DroneState['end']>): GameState {
  const d = state.drones?.find(x => x.id === droneId);
  if (!d || !d.alive) return state;
  return { ...state, drones: state.drones!.map(x => x.id === droneId ? { ...x, alive: false, end } : x) };
}

/** Fly every drone `dt` along its route; end those whose route or endurance is done. */
export function stepDrones(state: GameState, dt: number): { state: GameState; events: DroneEvent[] } {
  const live = (state.drones ?? []).filter(d => d.alive);
  if (live.length === 0) return { state, events: [] };
  const ps = personSetOf(state);
  const events: DroneEvent[] = [];
  // 62.19 (e): the weather where each drone is — a scout holds (hovers, its battery running), a strike not yet diving is
  // called back to where it was launched; a held scout flies on when the air clears
  const seconds = windSeconds(state);
  const weathered = (state.drones ?? []).map((d): DroneState => {
    if (!d.alive || d.dive || !state.config.weather) return d;
    const planet = state.planets[d.planetIndex];
    const why = planet ? droneWeather(state, d.planetIndex, droneGroundDir(d, planet.radius), seconds) : null;
    if (why && d.strike) {
      const here = droneGroundDir(d, planet.radius);
      events.push({ kind: 'droneGrounded', droneId: d.id, why, recalled: true });
      const { strike: _gone, ...rest } = d;
      return { ...rest, recalled: true as const, route: [here, d.route[0]], flown: 0 };
    }
    if (why && !d.strike && !d.recalled) { if (d.grounded !== why) events.push({ kind: 'droneGrounded', droneId: d.id, why }); return { ...d, grounded: why }; }
    if (!why && d.grounded) { events.push({ kind: 'droneCleared', droneId: d.id }); const { grounded: _g, ...rest } = d; return rest; }
    return d;
  });
  // 65.16: a diving strike drone is off its route (core/strikeDrone.ts flies it); it only ages. 62.19 (e): a held scout only ages
  let s: GameState = { ...state, drones: weathered.map(d => d.alive ? { ...d, ...(d.dive || d.grounded ? {} : { flown: d.flown + ps.droneSpeed * dt * droneWindFactor(state, d) }), age: d.age + dt } : d) };
  for (const d of s.drones!) {
    if (!d.alive || d.dive) continue;
    const total = legLengths(d.route, s.planets[d.planetIndex].radius).reduce((a, b) => a + b, 0);
    // a tasked strike drone at its route's end dives instead of ending (stepStrikeDrones, next)
    const end: DroneState['end'] | null = d.flown >= total ? (d.strike ? null : 'routeDone') : d.age >= ps.droneEndurance ? 'endurance' : null;
    if (end) { s = endDrone(s, d.id, end); events.push({ kind: 'droneSpent', droneId: d.id, end }); }
  }
  return { state: s, events };
}

/**
 * 62.52: how the wind speeds or slows a drone along its route — × (1 + ACTION_WIND.droneSpeed × the planet's gusting
 * wind along the drone's heading, against a storm's wind), clamped to ACTION_WIND.droneSpeedRange: slower upwind,
 * faster down. Up at its altitude the ground's shelter is not its (the gust is); its flight control holds it on its
 * route, so the wind costs or gives it time, never its line. 1 with the wind switch off or in calm air (a diving strike
 * drone corrects as it dives: core/strikeDrone.ts is untouched).
 */
export function droneWindFactor(state: GameState, d: DroneState): number {
  if (!state.config.wind) return 1;
  const planet = state.planets[d.planetIndex];
  if (!planet) return 1;
  const here = droneGroundDir(d, planet.radius);
  const w = gustedField(state, d.planetIndex, here, windSeconds(state));   // 62.54: the field where it flies, gusted (no ground shelter up there)
  if (!w) return 1;
  const ahead = droneGroundDir({ ...d, flown: d.flown + 1e-3 * planet.radius }, planet.radius);
  const way = vecSub(ahead, here);
  const len = vecLength(way);
  if (len < 1e-12) return 1;
  const along = vecDot(w, vecScale(way, 1 / len)) / stormWind(planet);
  const [lo, hi] = ACTION_WIND.droneSpeedRange;
  return Math.max(lo, Math.min(hi, 1 + ACTION_WIND.droneSpeed * along));
}

/** What the flying drones see now: enemies on their planet inside the ground radius, with a clear line from the drone. */
export function droneSightingsNow(state: GameState, tick: number): Sighting[] {
  const out: Sighting[] = [];
  const ps = personSetOf(state);
  for (const d of state.drones ?? []) {
    if (!d.alive) continue;
    const planet = state.planets[d.planetIndex];
    const ground = droneGroundDir(d, planet.radius);
    const eye = dronePos(d, state);
    for (const u of state.players) {
      if (!u.alive || (u.landingLeft ?? 0) > 0 || u.boarded || u.teamIndex === d.teamIndex || u.planetIndex !== d.planetIndex) continue;
      if (angle(vecNormalize(u.surfaceDir), ground) * planet.radius > ps.droneSightRadius) continue;
      const target = personEyePoint(u, state.planets, ps, ps.losEyeHeight);
      if (!personLineOfSight(eye, target, state)) continue;
      if (crownHides(state, d.planetIndex, eye, target, u.airborne ? undefined : u.stance)) continue;   // 66.7: under the crowns, unseen from above (foliageSight)
      if (out.some(o => o.teamIndex === d.teamIndex && o.unitId === u.id)) continue;
      out.push({ teamIndex: d.teamIndex, unitId: u.id, unitTeam: u.teamIndex, planetIndex: u.planetIndex, surfaceDir: { ...u.surfaceDir }, pos: personEyePoint(u, state.planets, ps, 0), tick, by: 'drone', sourceId: d.id });
    }
  }
  return out;
}
